import { Button, Divider, Select, Space, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { subscription, Subscription } from "../utils/http";
import { InputObject } from "./default";
import { emptyOption } from "./ComboBox.default";

export interface CreateableComboBoxProps {
  readonly className?: string;
  readonly readOnly?: boolean;
  readonly placeholder?: string;
  readonly value?: InputObject;
  readonly request: (search: string) => Promise<InputObject[]>;
  readonly onChange?: (value?: InputObject) => void;
  readonly disabled?: boolean;
  // Called when the user clicks "+ Create new" in the dropdown. Receives the
  // current search text as a convenient initial Name. Must return the newly-
  // created option (with the real server uid) so this combobox can auto-select
  // it. Throw to surface validation errors (duplicates, etc.) — the host
  // component is responsible for showing the message to the user.
  readonly onCreate?: (initialName: string) => Promise<InputObject | undefined>;
  readonly createLabel?: string;
  // Bump this value to force a reload of the options (e.g. after a role was
  // created in another row of a wizard list).
  readonly refreshKey?: string | number;
}

// Admin brief 2026-11-04 — ComboBox variant with an inline "+ Create new"
// affordance in the dropdown. Kept as a separate component so the shared
// ComboBox.tsx (used in dozens of places) stays untouched.
function CreateableComboBox(props: CreateableComboBoxProps) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<
    { readonly value?: any; readonly label: string; readonly subLabel?: string; readonly dataItem?: any }[]
  >([emptyOption as any]);
  const [search, setSearch] = useState("");
  const [subscribe, setSubscribe] = useState<Subscription | null>(null);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // refreshKey change — reload the current result set so new rows made
    // elsewhere in the wizard appear.
    loadData(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.refreshKey]);

  function onDropdownVisibleChange(next: boolean): void {
    setOpen(next);
    if (next) loadData("");
  }

  function onSearch(value: string): void {
    setSearch(value);
    loadData(value);
  }

  function onChange(_: any, option: any): void {
    const input: InputObject = {
      display: option.label,
      subDisplay: option.subLabel,
      value: option.value,
      dataItem: option.dataItem,
    };
    props.onChange?.(input);
  }

  function loadData(q: string): void {
    setOptions([emptyOption as any]);
    setLoading(true);
    subscribe?.unsubscribe();
    const $$ = subscription(() => props.request(q), 250);
    setSubscribe($$);
    $$.then((data) => {
      const mapped = (data || []).map((item) => ({
        label: item.display,
        subLabel: item.subDisplay,
        value: item.value,
        dataItem: item.dataItem,
      }));
      setOptions([emptyOption as any, ...mapped]);
      setLoading(false);
    }).catch(() => {
      setOptions([emptyOption as any]);
      setLoading(false);
    });
  }

  async function onCreateClick(): Promise<void> {
    if (!props.onCreate) return;
    try {
      setCreating(true);
      const created = await props.onCreate(search || "");
      if (created) {
        // Push into the current options and auto-select.
        setOptions((prev) => [
          ...prev,
          {
            label: created.display,
            subLabel: created.subDisplay,
            value: created.value,
            dataItem: created.dataItem,
          },
        ]);
        props.onChange?.(created);
        setOpen(false);
      }
    } finally {
      setCreating(false);
    }
  }

  return (
    <Select
      className={props.className}
      disabled={props.disabled}
      loading={loading || creating}
      style={props.readOnly ? { pointerEvents: "none" } : {}}
      value={
        props.value
          ? {
              label: props.value.display,
              subLabel: props.value.subDisplay,
              value: props.value.value,
              dataItem: props.value.dataItem,
            }
          : undefined
      }
      placeholder={props.placeholder}
      options={options as any}
      defaultActiveFirstOption={false}
      filterOption={false}
      notFoundContent={null}
      open={open}
      optionRender={(option) => (
        <Typography.Text className="inline-flex flex-col">
          <Typography.Text>{option.label}</Typography.Text>
          <Typography.Text type="secondary">{(option.data as any).subLabel}</Typography.Text>
        </Typography.Text>
      )}
      showSearch
      onChange={onChange}
      onSearch={onSearch}
      onDropdownVisibleChange={onDropdownVisibleChange}
      dropdownRender={(menu) => (
        <>
          {menu}
          {props.onCreate && (
            <>
              <Divider style={{ margin: "4px 0" }} />
              <Space className="w-full p-1">
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  loading={creating}
                  onClick={onCreateClick}
                  // Keep the dropdown open while the modal opens / closes.
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {props.createLabel ?? "Create new"}
                  {search ? ` "${search}"` : ""}
                </Button>
              </Space>
            </>
          )}
        </>
      )}
    />
  );
}

export default CreateableComboBox;
